<template>
  <div class="login-card">
    <h2 class="title">欢迎使用智磐</h2>

    <div class="tabs">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: loginMode === 'phone' }"
        @click="switchMode('phone')"
      >
        手机号登录
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: loginMode === 'account' }"
        @click="switchMode('account')"
      >
        账号登录
      </button>
    </div>

    <div v-if="loginMode === 'account'">
      <div class="form-group">
        <label>账号</label>
        <input
          type="text"
          v-model.trim="username"
          placeholder="请输入账号"
          class="login-input"
          autocomplete="username"
        />
      </div>
      <div class="form-group">
        <label>密码</label>
        <input
          type="password"
          v-model="password"
          placeholder="请输入密码"
          class="login-input"
          autocomplete="current-password"
          @keyup.enter="handleLogin"
        />
      </div>
    </div>

    <div v-else>
      <div class="form-group">
        <label>手机号</label>
        <input
          type="tel"
          v-model.trim="phone"
          placeholder="请输入11位手机号"
          class="login-input"
          autocomplete="tel"
          maxlength="11"
        />
      </div>
      <div class="form-group">
        <label>验证码</label>
        <div class="code-row">
          <input
            type="text"
            v-model.trim="smsCode"
            placeholder="请输入6位验证码"
            class="login-input"
            inputmode="numeric"
            maxlength="6"
            @keyup.enter="handleLogin"
          />
          <button
            type="button"
            class="code-btn"
            :disabled="smsCountdown > 0"
            @click="handleSendCode"
          >
            {{ smsCountdown > 0 ? `${smsCountdown}s后重试` : "获取验证码" }}
          </button>
        </div>
      </div>
    </div>

    <button class="submit-btn" :disabled="loading" @click="handleLogin">
      {{
        loading ? "登录中..." : loginMode === "phone" ? "登录 / 注册" : "登录"
      }}
    </button>

    <p v-if="hintMsg" class="hint-msg">{{ hintMsg }}</p>
    <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>

    <p class="agreement">登录即表示同意服务条款与隐私政策</p>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/useAuthStore";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const username = ref("");
const password = ref("");
const phone = ref("");
const smsCode = ref("");
const loginMode = ref("account");
const smsCountdown = ref(0);
const loading = ref(false);
const errorMsg = ref("");
const hintMsg = ref("");
let smsTimer = null;

const isValidPhone = (value) => /^1\d{10}$/.test(value);
const isValidSmsCode = (value) => /^\d{6}$/.test(value);

const clearSmsTimer = () => {
  if (smsTimer) {
    clearInterval(smsTimer);
    smsTimer = null;
  }
};

const startSmsCountdown = () => {
  clearSmsTimer();
  smsCountdown.value = 60;
  smsTimer = setInterval(() => {
    if (smsCountdown.value <= 1) {
      smsCountdown.value = 0;
      clearSmsTimer();
      return;
    }
    smsCountdown.value -= 1;
  }, 1000);
};

const switchMode = (mode) => {
  if (loginMode.value === mode) return;
  loginMode.value = mode;
  errorMsg.value = "";
  hintMsg.value = "";
};

const handleSendCode = async () => {
  errorMsg.value = "";
  hintMsg.value = "";
  if (!isValidPhone(phone.value)) {
    errorMsg.value = "请输入正确的11位手机号";
    return;
  }
  try {
    await authStore.sendSmsCode?.(phone.value);
    startSmsCountdown();
    hintMsg.value = "验证码已发送，请注意查收短信";
  } catch (error) {
    errorMsg.value = error.message || "验证码发送失败，请稍后重试";
  }
};

const handleLogin = async () => {
  errorMsg.value = "";
  hintMsg.value = "";
  if (loginMode.value === "phone") {
    if (!isValidPhone(phone.value)) {
      errorMsg.value = "请输入正确的11位手机号";
      return;
    }
    if (!isValidSmsCode(smsCode.value)) {
      errorMsg.value = "请输入6位数字验证码";
      return;
    }
    loading.value = true;
    try {
      await authStore.smsLogin(phone.value, smsCode.value);
      let targetPath = "/remove";
      if (route && route.query && route.query.redirect) {
        if (
          typeof route.query.redirect === "string" &&
          route.query.redirect.startsWith("/")
        ) {
          targetPath = route.query.redirect;
        }
      }
      router.push(targetPath);
    } catch (error) {
      errorMsg.value = error.message || "手机号登录失败，请稍后重试";
    } finally {
      loading.value = false;
    }
    return;
  }

  if (!username.value || !password.value) {
    errorMsg.value = "账号和密码不能为空";
    return;
  }

  loading.value = true;
  try {
    // 1. 调用登录接口
    await authStore.login(username.value, password.value);

    // 2. 终极安全跳转逻辑：层层判断，绝不报错
    let targetPath = "/remove"; // 默认安全跳转到工作台

    // 只有当 route 存在、query 存在，并且 redirect 存在时，才去读取它
    if (route && route.query && route.query.redirect) {
      if (
        typeof route.query.redirect === "string" &&
        route.query.redirect.startsWith("/")
      ) {
        targetPath = route.query.redirect;
      }
    }

    // 3. 执行跳转
    router.push(targetPath);
  } catch (error) {
    errorMsg.value = error.message || "登录失败，请检查账号密码";
  } finally {
    loading.value = false;
  }
};

onBeforeUnmount(() => {
  clearSmsTimer();
});
</script>

<style scoped>
.login-card {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 253, 251, 0.92);
  border: 1px solid rgba(62, 47, 33, 0.1);
  border-radius: 20px;
  padding: 2rem 1.8rem;
  box-shadow: 0 18px 34px -18px rgba(44, 33, 21, 0.32);
}

.title {
  text-align: center;
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #1f1f1f;
}

.tabs {
  margin-top: 1rem;
  display: flex;
  gap: 0.35rem;
  background: #f2ece4;
  border-radius: 12px;
  padding: 0.25rem;
}

.tab-btn {
  flex: 1;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #7b7168;
  font-size: 0.9rem;
  padding: 0.55rem 0.75rem;
  cursor: pointer;
}

.tab-btn.active {
  background: #fffdf9;
  color: #2c241a;
  box-shadow: 0 3px 8px rgba(44, 33, 21, 0.08);
}

.form-group {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-group label {
  font-size: 0.88rem;
  color: #61584f;
}

.login-input {
  width: 100%;
  padding: 0.75rem 0.9rem;
  border: 1px solid #d8cbbb;
  border-radius: 10px;
  background: #fffdfb;
  outline: none;
  box-sizing: border-box;
  font-size: 0.94rem;
}

.login-input:focus {
  border-color: #c99658;
  box-shadow: 0 0 0 3px rgba(201, 150, 88, 0.14);
}

.code-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.6rem;
}

.code-btn {
  border: 1px solid #d8cbbb;
  border-radius: 10px;
  padding: 0 0.9rem;
  background: #fff9f2;
  color: #7b5f40;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
}

.code-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-btn {
  width: 100%;
  margin-top: 1.35rem;
  padding: 0.82rem 1rem;
  background: #1a1a1a;
  color: #fff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: background 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  background: #313131;
}

.submit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.hint-msg {
  color: #8b6b45;
  font-size: 0.84rem;
  text-align: left;
  margin: 0.8rem 0 0;
}

.error-msg {
  color: #d84b4b;
  font-size: 0.86rem;
  text-align: left;
  margin: 0.8rem 0 0;
}

.agreement {
  margin: 0.95rem 0 0;
  font-size: 0.76rem;
  color: #95887a;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .login-card {
    margin-bottom: 2rem;
  }
}
</style>
